import React, { useState, useEffect, useMemo } from "react";
import ManagerDataService from "../services/manager.service";
import AdminDataService from "../services/admin.service";
import { useAuth0 } from '@auth0/auth0-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid, regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import DataTable from 'react-data-table-component';
import Select from 'react-select';

const TABLES = {
  assureurs: {
    key: 'assureurs',
    title: 'Assureurs',
    path: '/admin/assureurs',
    idType: 'int',
    fields: [
      { name: 'libelle_assureur', label: 'Libellé', type: 'text', max: 50 }
    ]
  },
  prestations: {
    key: 'prestations', title: 'Prestations', path: '/admin/prestations', idType: 'int',
    fields: [ { name: 'libelle_prestation', label: 'Libellé', type: 'text', max: 50 } ]
  },
  secteurs: {
    key: 'secteurs', title: 'Secteurs', path: '/admin/secteurs', idType: 'int',
    fields: [ { name: 'libelle_secteur', label: 'Libellé', type: 'text', max: 50 } ]
  },
  specialites: {
    key: 'specialites', title: 'Spécialités', path: '/admin/specialites', idType: 'int',
    fields: [ { name: 'libelle_specialite', label: 'Libellé', type: 'text', max: 50 } ]
  },
  'type-sub-terr': {
    key: 'type-sub-terr', title: 'Types de Localité', path: '/admin/type-sub-terr', idType: 'int',
    fields: [ { name: 'libelle_type_sub_terr', label: 'Libellé', type: 'text', max: 50 } ]
  },
  'sub-terr': {
    key: 'sub-terr', title: 'Localités', path: '/admin/sub-terr', idType: 'string',
    fields: [
      { name: 'id', label: 'ID (string)', type: 'text', max: 30, createOnly: true },
      { name: 'id_type_sub_terr', label: 'Type', type: 'number' },
      { name: 'libelle_sub_terr', label: 'Libellé', type: 'text', max: 60 },
      { name: 'tutelle', label: 'Tutelle', type: 'text', max: 60, optional: true }
    ]
  },
  'type-structures': {
    key: 'type-structures', title: 'Types de Structures', path: '/admin/type-structures', idType: 'int',
    fields: [
      { name: 'libelle_type_structure', label: 'Libellé', type: 'text', max: 60 },
      { name: 'id_secteur', label: 'Secteur', type: 'number' }
    ]
  },
  structures: {
    key: 'structures', title: 'Structures', path: '/admin/structures', idType: 'int',
    fields: [
      { name: 'libelle_structure', label: 'Libellé', type: 'text', max: 60, required: true },
      { name: 'sigle_structure', label: 'Sigle', type: 'text', max: 10, optional: true },
      { name: 'contact', label: 'Contact', type: 'text', max: 50, optional: true },
      { name: 'periode_prise_rdv', label: 'Délai RDV (en mois)', type: 'number', required: true },
      { name: 'id_type_structure', label: 'Type structure', type: 'number', required: true },
      { name: 'id_sub_terr', label: 'Localisation', type: 'text', max: 30, required: true }
    ]
  },
  services: {
    key: 'services', title: 'Services', path: '/admin/services', idType: 'int',
    fields: [
      { name: 'libelle_service', label: 'Libellé', type: 'text', max: 60 },
      { name: 'id_structure', label: 'Structure', type: 'number' }
    ]
  },
  users: {
    key: 'users', title: 'Utilisateurs', path: '/admin/users', idType: 'int',
    fields: [
      { name: 'nom', label: 'Nom', type: 'text', max: 60 },
      { name: 'prenom', label: 'Prénom', type: 'text', max: 60 },
      { name: 'sexe', label: 'Sexe', type: 'text', max: 10 },
      { name: 'date_naiss', label: 'Date de naissance', type: 'text', max: 10 },
      { name: 'nationalite', label: 'Nationalité', type: 'text', max: 60 },
      { name: 'residence', label: 'Résidence', type: 'text', max: 60 },
      { name: 'tel', label: 'Téléphone', type: 'text', max: 30 },
      { name: 'email', label: 'Email', type: 'text', max: 120 }
    ]
  }
};

// audit fields are resolved server-side from the connected user's email

const HomeAdmin = () => {
  const { getAccessTokenSilently, user, isLoading } = useAuth0();
  const [habilitation, setHabilitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');

  const tabs = useMemo(() => Object.values(TABLES), []);
  const [activeKey, setActiveKey] = useState(tabs[0].key);

  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [cache, setCache] = useState({});
  const [secteurs, setSecteurs] = useState([]);
  const [typeStructOptions, setTypeStructOptions] = useState([]);
  const [subTerrOptions, setSubTerrOptions] = useState([]);
  const [structureOptions, setStructureOptions] = useState([]);
  const [showLocModal, setShowLocModal] = useState(false);
  const [locForm, setLocForm] = useState({ loc_lo: '', loc_la: '' });
  const [locTargetId, setLocTargetId] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const getErrorMessage = (err) => {
    const backendMsg = err?.response?.data?.message;
    const validation = err?.response?.data?.errors;
    if (backendMsg && Array.isArray(validation) && validation.length) {
      return backendMsg + " - " + validation.map(e => e.msg).join(", ");
    }
    return backendMsg || err?.message || "Erreur inconnue";
  };
  const activeTable = useMemo(() => TABLES[activeKey], [activeKey]);

  useEffect(() => {
    const fetchHabilitation = async () => {
      try {
        const token = await getAccessTokenSilently();
        await new Promise(r => setTimeout(r, 100));
        const response = await ManagerDataService.habilitation(user.email);
        setHabilitation(response.data[0] || {});
      } catch (e) {
        setHabilitation({});
      } finally {
        setLoading(false);
      }
    };
    if (!isLoading && user?.email) fetchHabilitation();
    else if (!isLoading && !user?.email) setLoading(false);
  }, [user?.email, isLoading, getAccessTokenSilently]);

  useEffect(() => {
    const loadSecteurs = async () => {
      try {
        if (activeTable.key === 'type-structures') {
          const res = await AdminDataService.list('secteurs');
          setSecteurs(Array.isArray(res.data) ? res.data : []);
        }
      } catch (e) { /* noop */ }
    };
    loadSecteurs();
  }, [activeTable.key]);

  useEffect(() => {
    const loadStructureSelects = async () => {
      try {
        if (activeTable.key === 'structures') {
          const [resTypes, resSubs] = await Promise.all([
            AdminDataService.list('type-structures'),
            AdminDataService.list('sub-terr')
          ]);
          const typeOpts = Array.isArray(resTypes.data) ? resTypes.data.map(t => ({ value: t.id, label: t.libelle_type_structure })) : [];
          const subOpts = Array.isArray(resSubs.data) ? resSubs.data.map(s => ({ value: s.id, label: s.libelle_sub_terr })) : [];
          setTypeStructOptions(typeOpts);
          setSubTerrOptions(subOpts);
        }
      } catch (e) { /* noop */ }
    };
    loadStructureSelects();
  }, [activeTable.key]);

  useEffect(() => {
    const loadServiceSelects = async () => {
      try {
        if (activeTable.key === 'services') {
          const res = await AdminDataService.list('structures');
          const structOpts = Array.isArray(res.data) ? res.data.map(s => ({ value: s.id, label: s.libelle_structure })) : [];
          setStructureOptions(structOpts);
        }
      } catch (e) { /* noop */ }
    };
    loadServiceSelects();
  }, [activeTable.key]);

  const resetForm = () => {
    const base = {};
    activeTable.fields.forEach(f => {
      if (f.createOnly && editId) return;
      base[f.name] = '';
    });
    setForm({ ...base });
    setEditId(null);
  };

  const loadList = async () => {
    try {
      setBusy(true);
      // optimistic: show cached rows instantly if available
      if (cache[activeKey]) {
        setRows(cache[activeKey]);
      }
      const res = await AdminDataService.list(activeKey);
      const data = res.data || [];
      setRows(data);
      setCache(prev => ({ ...prev, [activeKey]: data }));
    } catch (e) {
      console.error('Load list error', e);
    } finally { setBusy(false); }
  };

  useEffect(() => { if (habilitation?.admin === 1) { resetForm(); loadList(); } }, [activeKey, habilitation?.admin]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const columns = useMemo(() => {
    const cols = [];

    // Only show ID for 'sub-terr'
    if (activeTable.key === 'sub-terr') {
      cols.push({
        id: '__id',
        name: 'ID',
        selector: row => row.id,
        sortable: true,
        width: '120px'
      });
    }

    // Build field columns. For 'sub-terr':
    // - afficher 'type_label' pour id_type_sub_terr
    // - injecter 'tutelle_label' après 'tutelle'
    const fieldCols = [];
    activeTable.fields
      .filter(f => !f.createOnly)
      .forEach(f => {
        if (activeTable.key === 'sub-terr' && f.name === 'id_type_sub_terr') {
          fieldCols.push({
            id: f.name,
            name: f.label,
            selector: row => row.type_label ?? row.id_type_sub_terr,
            sortable: true,
          });
          return;
        }
        // For 'type-structures': afficher libelle_secteur au lieu de id_secteur
        if (activeTable.key === 'type-structures' && f.name === 'id_secteur') {
          fieldCols.push({
            id: f.name,
            name: f.label,
            selector: row => row.libelle_secteur ?? row.id_secteur,
            sortable: true,
          });
          return;
        }
        // For 'services': afficher libelle_structure au lieu de id_structure
        if (activeTable.key === 'services' && f.name === 'id_structure') {
          fieldCols.push({
            id: f.name,
            name: f.label,
            selector: row => row.libelle_structure ?? row.id_structure,
            sortable: true,
          });
          return;
        }
        // For 'users': format date_naiss as YYYY-MM-DD
        if (activeTable.key === 'users' && f.name === 'date_naiss') {
          fieldCols.push({
            id: f.name,
            name: f.label,
            selector: row => (row.date_naiss ? String(row.date_naiss).slice(0, 10) : ''),
            sortable: true,
            width: '150px'
          });
          return;
        }
        // For 'structures': masquer la colonne Type structure
        if (activeTable.key === 'structures' && f.name === 'id_type_structure') {
          return;
        }
        if (activeTable.key === 'structures' && f.name === 'id_sub_terr') {
          fieldCols.push({
            id: f.name,
            name: 'Localité',
            selector: row => row.libelle_sub_terr ?? row.id_sub_terr,
            sortable: true,
          });
          return;
        }
        const col = {
          id: f.name,
          name: f.label,
          selector: row => row[f.name],
          sortable: true,
        };
        if (f.name.startsWith('libelle')) {
          col.grow = 2;
          col.minWidth = '280px';
        }
        fieldCols.push(col);
        if (activeTable.key === 'sub-terr' && f.name === 'tutelle') {
          fieldCols.push({
            id: '__libelle_tutelle',
            name: 'Libellé tutelle',
            selector: row => row.tutelle_label || '',
            sortable: true,
          });
        }
      });

    cols.push(...fieldCols);

    if (activeTable.key !== 'users') {
      cols.push({
        id: '__actions',
        name: 'Actions',
        cell: row => (
          <div className="d-flex gap-2">
            {activeTable.key === 'structures' && (
              <>
                <button
                  className="btn btn-sm btn-outline-primary bg-white text-primary border-primary"
                  style={{ backgroundColor: 'white', color: '#0d6efd', borderColor: '#0d6efd' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e9ecef'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                  onClick={() => window.open(`/show-structure?id=${row.id}`, '_self')}
                  disabled={busy}
                  title="Ouvrir"
                  aria-label="Ouvrir"
                >
                  <FontAwesomeIcon icon={solid('right-to-bracket')} />
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary bg-white text-secondary border-secondary"
                  style={{ backgroundColor: 'white', color: '#6c757d', borderColor: '#6c757d' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e9ecef'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                  onClick={() => openLoc(row)}
                  disabled={busy}
                  title="Localiser"
                  aria-label="Localiser"
                >
                  <FontAwesomeIcon icon={solid('location-dot')} />
                </button>
              </>
            )}
            <button className="btn btn-sm btn-secondary" onClick={() => onEdit(row)} disabled={busy} title="Éditer" aria-label="Éditer"><FontAwesomeIcon icon={regular('pen-to-square')} /></button>
            <button className="btn btn-sm btn-danger" onClick={() => onDelete(row.id)} disabled={busy} title="Supprimer" aria-label="Supprimer"><FontAwesomeIcon icon={regular('trash-can')} /></button>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: '190px'
      });
    } else {
      // Special Actions column for users: open user profile by email
      cols.push({
        id: '__open_profile',
        name: 'Actions',
        cell: row => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-primary bg-white text-primary border-primary"
              style={{ backgroundColor: 'white', color: '#0d6efd', borderColor: '#0d6efd' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e9ecef'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
              onClick={() => window.open(`/user-profil?email=${encodeURIComponent(row.email)}`, '_self')}
              disabled={busy}
              title="Ouvrir le profil"
              aria-label="Ouvrir le profil"
            >
              <FontAwesomeIcon icon={solid('right-to-bracket')} />
            </button>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: '120px'
      });
    }

    return cols;
  }, [activeTable, busy]);

  const filteredRows = useMemo(() => {
    if (!filterText) return rows;
    const q = filterText.toLowerCase();
    const names = activeTable.fields.filter(f => !f.createOnly).map(f => f.name);
    return rows.filter(r => {
      if (String(r.id).toLowerCase().includes(q)) return true;
      return names.some(n => String(r[n] ?? '').toLowerCase().includes(q));
    });
  }, [rows, filterText, activeTable]);

  const onEdit = (row) => {
    setEditId(row.id);
    const base = {};
    activeTable.fields.forEach(f => { if (!f.createOnly) base[f.name] = row[f.name] ?? ''; });
    setForm({ ...base });
    setShowModal(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm('Confirmer la suppression ?')) return;
    try {
      setBusy(true);
      await AdminDataService.delete(activeKey, id);
      await loadList();
    } catch (e) { setErrorMsg(getErrorMessage(e)); }
    finally { setBusy(false); }
  };
  const openLoc = (row) => {
    setLocTargetId(row.id);
    const locText = row.loc || '';
    let lon = '', lat = '';
    const m = /^POINT\(([-+]?\d*\.?\d+)\s+([-+]?\d*\.?\d+)\)$/i.exec(String(locText).trim());
    if (m) { lon = m[1]; lat = m[2]; }
    setLocForm({ loc_lo: lon, loc_la: lat });
    setShowLocModal(true);
  };
  const onLocChange = (e) => {
    const { name, value } = e.target;
    setLocForm(prev => ({ ...prev, [name]: value }));
  };
  const fillWithGeolocation = () => {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      alert('Géolocalisation non supportée par ce navigateur.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setLocForm({ loc_lo: String(longitude), loc_la: String(latitude) });
      },
      () => {
        alert('Impossible de récupérer la position actuelle.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };
  const clearLoc = () => { setLocForm({ loc_lo: '', loc_la: '' }); };
  const submitLoc = async (e) => {
    e.preventDefault();
    try {
      setBusy(true);
      const email = user?.email;
      if (!email) { alert("Impossible de récupérer l'email de l'utilisateur connecté."); return; }
      const payload = { ...locForm, email };
      await AdminDataService.updateStructureLoc(locTargetId, payload);
      setShowLocModal(false);
      setLocTargetId(null);
      await loadList();
    } catch (e) {
      console.error('Update loc error', e);
      alert(e?.response?.data?.message || 'Erreur de mise à jour de la localisation');
    } finally { setBusy(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      setBusy(true);
      const email = user?.email;
      if (!email) {
        alert("Impossible de récupérer l'email de l'utilisateur connecté.");
        return;
      }
      if (activeTable.key === 'type-structures') {
        const lib = (form.libelle_type_structure || '').trim();
        const sec = form.id_secteur;
        if (!lib || !sec) { alert('Veuillez renseigner le libellé et le secteur.'); setBusy(false); return; }
      }
      if (activeTable.key === 'services') {
        const lib = (form.libelle_service || '').trim();
        const structId = form.id_structure;
        if (!lib || !structId) { alert('Veuillez renseigner le libellé et la structure.'); setBusy(false); return; }
      }
      if (activeTable.key === 'assureurs') {
        const lib = (form.libelle_assureur || '').trim();
        if (!lib) { alert('Veuillez renseigner le libellé de l\'assureur.'); setBusy(false); return; }
      }
      if (activeTable.key === 'prestations') {
        const lib = (form.libelle_prestation || '').trim();
        if (!lib) { alert('Veuillez renseigner le libellé de la prestation.'); setBusy(false); return; }
      }
      if (activeTable.key === 'secteurs') {
        const lib = (form.libelle_secteur || '').trim();
        if (!lib) { alert('Veuillez renseigner le libellé du secteur.'); setBusy(false); return; }
      }
      if (activeTable.key === 'specialites') {
        const lib = (form.libelle_specialite || '').trim();
        if (!lib) { alert('Veuillez renseigner le libellé de la spécialité.'); setBusy(false); return; }
      }
      if (activeTable.key === 'type-sub-terr') {
        const lib = (form.libelle_type_sub_terr || '').trim();
        if (!lib) { alert('Veuillez renseigner le libellé du type de Sub Terr.'); setBusy(false); return; }
      }
      if (editId == null) {
        // CREATE -> send email for server-side audit resolution
        const payload = { ...form, email };
        const res = await AdminDataService.create(activeKey, payload);
        if (res.status === 201) resetForm();
      } else {
        // UPDATE -> send email for server-side audit resolution
        const payload = { ...form, email };
        const idParam = activeTable.idType === 'string' ? editId : Number(editId);
        await AdminDataService.update(activeKey, idParam, payload);
        setEditId(null);
      }
      await loadList();
    } catch (e) {
      console.error('Submit error', e);
      alert(e?.response?.data?.message || 'Erreur de soumission');
    } finally { setBusy(false); }
  };

  const isStructureValid = useMemo(() => {
    if (activeTable.key !== 'structures') return true;
    const lib = (form.libelle_structure || '').trim();
    const delai = form.periode_prise_rdv;
    const typeId = form.id_type_structure;
    const sub = form.id_sub_terr;
    return !!(lib && delai && typeId && sub);
  }, [activeTable.key, form]);

  const isServiceValid = useMemo(() => {
    if (activeTable.key !== 'services') return true;
    const lib = (form.libelle_service || '').trim();
    const structId = form.id_structure;
    return !!(lib && structId);
  }, [activeTable.key, form]);

  return (
    <div className="col-md-12 mx-auto">
      <div className="row mx-auto" style={{ backgroundColor: "#6f42c1", color: "white", padding: "20px" }}>
        <span style={{ fontSize: "1.5rem" }} className="lead"><FontAwesomeIcon icon={solid('gears')} size="2x"/> <b>Paramétrage des tables de référence</b></span>
      </div>
      <div className="row mx-auto" style={{ padding: "20px" }}>

        {/* explicit flags to avoid nested ternaries */}
        {(() => {
          const ready = !loading && habilitation != null;
          const canView = ready && habilitation?.admin === 1;
          return (
            <>
              {!ready && (
                <div className="mt-2 text-muted">Chargement des droits...</div>
              )}

              {ready && !canView && (
                <div className="mt-4">
                  <div className="alert alert-danger d-flex align-items-center" role="alert">
                    <span className="me-2"><FontAwesomeIcon icon={solid('triangle-exclamation')} /></span>
                    <div><strong>Accès interdit.</strong> Vous n'avez pas les droits nécessaires pour accéder à cette section.</div>
                  </div>
                </div>
              )}

              {canView && (
                <>
                  <ul className="nav nav-tabs" id="adminTab" role="tablist">
                    {tabs.map((t, idx) => (
                      <li className="nav-item" role="presentation" key={t.key}>
                        <button
                          className={`nav-link ${activeKey === t.key ? 'active' : ''}`}
                          id={`${t.key}-tab`}
                          data-bs-toggle="tab"
                          data-bs-target={`#${t.key}-pane`}
                          type="button"
                          role="tab"
                          aria-controls={`${t.key}-pane`}
                          aria-selected={activeKey === t.key}
                          onClick={() => setActiveKey(t.key)}
                        >{t.title}</button>
                      </li>
                    ))}
                  </ul>

                  <div className="tab-content" id="adminTabContent">
                    {tabs.map((t) => (
                      <div
                        key={t.key}
                        className={`tab-pane fade ${activeKey === t.key ? 'show active' : ''}`}
                        id={`${t.key}-pane`}
                        role="tabpanel"
                        aria-labelledby={`${t.key}-tab`}
                        tabIndex="0"
                      >
                        {activeKey === t.key && (
                          <div className="row mt-3">
                            <div className="col-12">
                              <div className="d-flex align-items-center mb-2">
                                <div className="d-flex align-items-center gap-2">
                                  <input type="text" className="form-control form-control-sm" style={{ width: 220 }} placeholder="Rechercher..." value={filterText} onChange={(e) => setFilterText(e.target.value)} />
                                  <button className="btn btn-sm btn-outline-primary" onClick={loadList} disabled={busy} title="Rafraîchir" aria-label="Rafraîchir"><FontAwesomeIcon icon={solid('rotate')} /></button>
                                  {activeTable.key !== 'users' && (
                                    <button className="btn btn-sm btn-primary" onClick={() => { resetForm(); setShowModal(true); }} disabled={busy} title="Nouveau" aria-label="Nouveau"><FontAwesomeIcon icon={solid('plus')} /></button>
                                  )}
                                </div>
                              </div>
                              <DataTable
                                columns={columns}
                                data={filteredRows}
                                dense
                                pagination
                                highlightOnHover
                                progressPending={busy}
                                responsive
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          );
        })()}
        {showLocModal && (
          <>
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Mettre à jour la localisation #{locTargetId}</h5>
                    <button type="button" className="btn-close" onClick={() => setShowLocModal(false)} aria-label="Close"></button>
                  </div>
                  <form onSubmit={submitLoc}>
                    <div className="modal-body">
                      <div className="d-flex justify-content-end mb-2 gap-2">
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={fillWithGeolocation} title="Récupérer ma position" aria-label="Récupérer ma position">
                          <FontAwesomeIcon icon={solid('rotate')} />
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={clearLoc} title="Effacer" aria-label="Effacer">
                          <FontAwesomeIcon icon={solid('xmark')} />
                        </button>
                      </div>
                      <div className="mb-2">
                        <label className="form-label">Longitude</label>
                        <input type="number" step="any" name="loc_lo" value={locForm.loc_lo} onChange={onLocChange} className="form-control form-control-sm" placeholder="Ex: -3.987" readOnly />
                      </div>
                      <div className="mb-2">
                        <label className="form-label">Latitude</label>
                        <input type="number" step="any" name="loc_la" value={locForm.loc_la} onChange={onLocChange} className="form-control form-control-sm" placeholder="Ex: 5.321" readOnly />
                      </div>
                      <div className="text-muted" style={{fontSize: '0.85rem'}}>Laissez vide pour effacer la localisation.</div>
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-secondary" onClick={() => setShowLocModal(false)} disabled={busy}>Annuler</button>
                      <button type="submit" className="btn btn-primary" disabled={busy}>Enregistrer</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show"></div>
          </>
        )}

        {showModal && (
          <>
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{editId == null ? `Créer ${activeTable.title}` : `Mettre à jour ${activeTable.title} #${editId}`}</h5>
                    <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close"></button>
                  </div>
                  <form onSubmit={async (e) => { await submit(e); if (!busy) setShowModal(false); }}>
                    <div className="modal-body">
                      {activeTable.fields.map(f => {
                        if (f.createOnly && editId) return null;
                        return (
                          <div className="mb-2" key={f.name}>
                            <label className="form-label">{f.label}</label>
                            {activeTable.key === 'type-structures' && f.name === 'id_secteur' ? (
                              <select
                                name={f.name}
                                value={form[f.name] ?? ''}
                                onChange={onChange}
                                className="form-select form-select-sm"
                                required
                              >
                                <option value="">Sélectionner un secteur</option>
                                {secteurs.map(s => (
                                  <option key={s.id} value={s.id}>{s.libelle_secteur}</option>
                                ))}
                              </select>
                            ) : activeTable.key === 'services' && f.name === 'id_structure' ? (
                              <Select
                                classNamePrefix="select"
                                isClearable
                                isSearchable
                                placeholder="Sélectionner la structure"
                                options={structureOptions}
                                value={structureOptions.find(o => String(o.value) === String(form[f.name] ?? '')) || null}
                                onChange={(e) => setForm(prev => ({ ...prev, [f.name]: e ? e.value : '' }))}
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                noOptionsMessage={() => 'Aucune structure trouvée'}
                              />
                            ) : activeTable.key === 'structures' && f.name === 'id_type_structure' ? (
                              <Select
                                classNamePrefix="select"
                                isClearable
                                isSearchable
                                placeholder="Sélectionner le type de structure"
                                options={typeStructOptions}
                                value={typeStructOptions.find(o => String(o.value) === String(form[f.name] ?? '')) || null}
                                onChange={(e) => setForm(prev => ({ ...prev, [f.name]: e ? e.value : '' }))}
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                noOptionsMessage={() => 'Aucun type trouvé'}
                              />
                            ) : activeTable.key === 'structures' && f.name === 'id_sub_terr' ? (
                              <Select
                                classNamePrefix="select"
                                isClearable
                                isSearchable
                                placeholder="Sélectionner la localisation"
                                options={subTerrOptions}
                                value={subTerrOptions.find(o => String(o.value) === String(form[f.name] ?? '')) || null}
                                onChange={(e) => setForm(prev => ({ ...prev, [f.name]: e ? e.value : '' }))}
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                noOptionsMessage={() => 'Aucune localisation trouvée'}
                              />
                            ) : (
                              <input
                                type={f.type === 'number' ? 'number' : 'text'}
                                name={f.name}
                                value={form[f.name] ?? ''}
                                onChange={onChange}
                                className="form-control form-control-sm"
                                maxLength={f.max || undefined}
                                placeholder={f.optional ? '(optionnel)' : ''}
                                required={
                                  (activeTable.key === 'type-structures' && f.name === 'libelle_type_structure') ||
                                  (activeTable.key === 'structures' && (f.name === 'libelle_structure' || f.name === 'periode_prise_rdv')) ||
                                  (activeTable.key === 'services' && f.name === 'libelle_service') ||
                                  (activeTable.key === 'assureurs' && f.name === 'libelle_assureur') ||
                                  (activeTable.key === 'prestations' && f.name === 'libelle_prestation') ||
                                  (activeTable.key === 'secteurs' && f.name === 'libelle_secteur') ||
                                  (activeTable.key === 'specialites' && f.name === 'libelle_specialite') ||
                                  (activeTable.key === 'type-sub-terr' && f.name === 'libelle_type_sub_terr')
                                }
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={busy}>Annuler</button>
                      <button type="submit" className="btn btn-primary" disabled={busy || !isStructureValid || (activeTable.key === 'services' && !isServiceValid)}>{editId == null ? 'Créer' : 'Mettre à jour'}</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show"></div>
          </>
        )}
        {errorMsg && (
          <>
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Erreur</h5>
                    <button type="button" className="btn-close" onClick={() => setErrorMsg("")} aria-label="Close"></button>
                  </div>
                  <div className="modal-body">
                    <div className="alert alert-danger mb-0" role="alert" style={{ whiteSpace: 'pre-wrap' }}>{errorMsg}</div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setErrorMsg("")}>Fermer</button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show"></div>
          </>
        )}
      </div>
    </div>
  );
};

export default HomeAdmin;